/*
 * Glow House — theme.js (vanilla, no libraries)
 *
 * PUBLIC CONTRACT (other sections/snippets rely on this):
 *
 * window.GH                     Created in layout/theme.liquid with GH.routes, GH.settings, GH.strings.
 *
 * GH.cart.add(items)            items = [{ id, quantity, properties? }] or a FormData (normal /cart/add form).
 *                               POSTs to routes.cartAdd + '.js', then GH.cart.refresh(). Opens the cart drawer
 *                               when GH.settings.openCartOnAdd, else GH.toast(GH.strings.added).
 *                               On error: toast with the error description; the promise rejects.
 *                               Returns Promise<cart> (the /cart.js object).
 * GH.cart.change(lineOrKey, quantity)
 *                               number = 1-based line, string = line item key. Same refresh flow. Promise<cart>.
 * GH.cart.refresh()             Re-renders the cart-drawer section (Section Rendering API ?sections=cart-drawer),
 *                               updates every [data-cart-count] (hidden at 0 when it has [data-hide-zero]) and
 *                               dispatches document event 'gh:cart:updated' (detail: { cart }). Promise<cart>.
 * [data-add-to-cart="<variantId>"] (optional data-quantity)
 *                               Click → GH.cart.add. Button is disabled (and .is-loading) while pending.
 * form[data-product-form]       Submit (name="id", name="quantity", properties[...]) → GH.cart.add(FormData).
 * [data-cart-change="<line key>"][data-quantity="<n>"]
 *                               Click → GH.cart.change(key, n). input[data-cart-qty-input="<key>"] on change too.
 *
 * GH.toast(message)             Short message at the bottom of the screen (in [data-toast-root]).
 *
 * GH.drawer.open(name, opener?) / GH.drawer.close(name?) / GH.drawer.isOpen(name)
 *                               Overlays are [data-drawer="<name>"]: 'cart', 'favorites', 'menu', 'contact',
 *                               'withdrawal', 'scratch'. Any [data-open="<name>"] opens it; any [data-close]
 *                               inside closes it. Focus trap, Escape closes, body.gh-locked while open,
 *                               focus returns to the opener. Events: 'gh:drawer:open' / 'gh:drawer:close'
 *                               (detail: { name }).
 *                               [data-open="menu"][data-menu-panel="<view>"] opens the mobile menu on a given
 *                               panel: 'cats' (default), 'brands', or 'cat-<n>' (n = 1-based top-level link).
 *
 * Favourites                    [data-wish] buttons (snippets/wish-button.liquid) toggle the product in
 *                               localStorage 'gh:favorites' (array of {handle,title,url,image,price,compare,variant}),
 *                               set aria-pressed on all matching buttons, update [data-fav-count] and render the
 *                               favourites drawer. GH.favorites = { get, has, toggle, remove, sync, render }.
 *                               New [data-wish] buttons added to the DOM later are synced automatically.
 *
 * Carousels                     [data-carousel] containing a scroll track [data-carousel-track] (.gh-row) and
 *                               [data-carousel-prev]/[data-carousel-next]. Arrows scroll one viewport of cards;
 *                               the track is resolved at click time (it may move between rows); arrows are
 *                               disabled at the ends (recomputed on scroll/resize). GH.carousels.update(root?).
 *
 * Accordions                    button[aria-expanded][data-accordion] toggles aria-expanded (CSS opens the
 *                               following .gh-acc__panel).
 * Quantity steppers             [data-qty] wrapper with [data-qty-minus], [data-qty-plus] and an input;
 *                               clamped to min (default 1) / max; dispatches 'change' on the input.
 *
 * Everything uses event delegation on document, so re-rendered sections keep working.
 */
(function () {
  'use strict';

  var GH = (window.GH = window.GH || {});
  GH.routes = GH.routes || {};
  GH.settings = GH.settings || {};
  GH.strings = GH.strings || {};

  var routes = {
    cart: GH.routes.cart || '/cart',
    cartAdd: GH.routes.cartAdd || '/cart/add',
    cartChange: GH.routes.cartChange || '/cart/change',
    predictiveSearch: GH.routes.predictiveSearch || '/search/suggest'
  };

  /* ---------------------------------------------------------------- helpers */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function str(key, fallback) { return GH.strings[key] || fallback || ''; }
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }
  function emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); }
  function fill(template, map) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  var storage = {
    mem: {},
    get: function (key) {
      try { return window.localStorage.getItem(key); } catch (e) { return this.mem[key] == null ? null : this.mem[key]; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) { this.mem[key] = value; }
    }
  };
  function isDesktop() { return window.matchMedia ? window.matchMedia('(min-width: 990px)').matches : true; }
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
  function visibleFocusables(root) {
    return $$(FOCUSABLE, root).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
  }

  /* ------------------------------------------------------------------ toast */
  var toastTimer;
  GH.toast = function (message) {
    if (!message) return;
    var root = $('[data-toast-root]');
    if (!root) {
      root = document.createElement('div');
      root.setAttribute('data-toast-root', '');
      root.setAttribute('aria-live', 'polite');
      document.body.appendChild(root);
    }
    clearTimeout(toastTimer);
    root.innerHTML = '';
    var el = document.createElement('div');
    el.className = 'gh-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    root.appendChild(el);
    toastTimer = setTimeout(function () {
      el.classList.add('is-leaving');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 220);
    }, 2600);
  };

  /* ---------------------------------------------------------------- drawers */
  var openStack = []; // [{ name, el, opener }]

  function overlay(name) { return $('[data-drawer="' + name + '"]'); }
  function panelOf(el) { return $('[role="dialog"]', el) || el; }

  // Locked while an overlay is open, or while the full-screen mobile search is open. The scrollbar
  // width is added as padding so the page doesn't shift sideways when overflow: hidden removes it.
  var searchLock = false;
  function lockBody() {
    var body = document.body;
    var lock = openStack.length > 0 || searchLock;
    var was = body.classList.contains('gh-locked');
    if (lock && !was) {
      var sw = window.innerWidth - document.documentElement.clientWidth;
      body.style.paddingRight = sw > 0 ? sw + 'px' : '';
      document.documentElement.style.setProperty('--gh-scrollbar', (sw > 0 ? sw : 0) + 'px');
    } else if (!lock && was) {
      body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--gh-scrollbar');
    }
    body.classList.toggle('gh-locked', lock);
  }

  GH.drawer = {
    isOpen: function (name) {
      return openStack.some(function (o) { return o.name === name; });
    },
    open: function (name, opener) {
      var el = overlay(name);
      if (!el) return false;
      if (GH.drawer.isOpen(name)) return true;
      // only one overlay at a time: close the others without restoring focus
      openStack.slice().forEach(function (o) { closeEntry(o, false); });
      closeSearch();
      closeMega();
      var entry = { name: name, el: el, opener: opener || document.activeElement };
      openStack.push(entry);
      clearTimeout(el._ghLeave);
      el.classList.remove('is-leaving');
      el.hidden = false;
      lockBody();
      showHeader();
      var panel = panelOf(el);
      var target = $('[autofocus]', panel) || panel;
      if (target === panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      requestAnimationFrame(function () {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      });
      emit('gh:drawer:open', { name: name, opener: entry.opener });
      return true;
    },
    close: function (name) {
      var entry;
      if (name) {
        entry = openStack.filter(function (o) { return o.name === name; })[0];
      } else {
        entry = openStack[openStack.length - 1];
      }
      if (entry) closeEntry(entry, true);
    }
  };

  function closeEntry(entry, restoreFocus) {
    var idx = openStack.indexOf(entry);
    if (idx === -1) return;
    openStack.splice(idx, 1);
    var el = entry.el;
    el.classList.add('is-leaving');
    el._ghLeave = setTimeout(function () {
      el.hidden = true;
      el.classList.remove('is-leaving');
    }, 220);
    lockBody();
    if (restoreFocus && entry.opener && typeof entry.opener.focus === 'function' && document.contains(entry.opener)) {
      try { entry.opener.focus({ preventScroll: true }); } catch (e) { entry.opener.focus(); }
    }
    emit('gh:drawer:close', { name: entry.name });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open]');
    if (opener) {
      var name = opener.getAttribute('data-open');
      if (name === 'cart' && document.body.classList.contains('template-cart')) return; // follow the link
      if (overlay(name)) {
        e.preventDefault();
        if (name === 'menu') menuNextView = opener.getAttribute('data-menu-panel') || 'cats';
        GH.drawer.open(name, opener);
      }
      return;
    }
    var closer = e.target.closest('[data-close]');
    if (closer) {
      var ov = closer.closest('[data-drawer]');
      if (ov) {
        e.preventDefault();
        GH.drawer.close(ov.getAttribute('data-drawer'));
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!openStack.length) return;
    var top = openStack[openStack.length - 1];
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      GH.drawer.close(top.name);
      return;
    }
    if (e.key !== 'Tab') return;
    var panel = panelOf(top.el);
    var items = visibleFocusables(panel);
    if (!items.length) { e.preventDefault(); panel.focus(); return; }
    var first = items[0], last = items[items.length - 1];
    var active = document.activeElement;
    if (!panel.contains(active)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && (active === first || active === panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  /* ------------------------------------------------------------------- cart */
  function jsonFetch(url, options) {
    options = options || {};
    options.ghInternal = true; // ignored by fetch; tells the app-sync wrapper below this call is ours
    return fetch(url, options).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok || (data && data.status && Number(data.status) >= 400)) {
          var err = new Error((data && (data.description || data.message)) || str('error'));
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function updateCartCounts(count) {
    var n = parseInt(count, 10) || 0;
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = n > 99 ? '99+' : String(n); // long numbers would cover the icon
      el.classList.toggle('is-long', n > 99);
      if (el.hasAttribute('data-hide-zero')) el.hidden = !n;
    });
  }

  // Focus restore after a re-render: controls carry data-focus-id="<control>-<line index>". When that
  // line is gone (removed), try the same control on the line before, then the remove button.
  function findFocusTarget(root, id) {
    if (!id) return null;
    var q = function (v) { return $('[data-focus-id="' + v + '"]', root); };
    var m = /^(.*?)-(\d+)$/.exec(id);
    if (!m) return q(id);
    var n = parseInt(m[2], 10);
    var pre = m[1].replace(/-(minus|plus|qty|remove)$/, '');
    return q(id) || q(pre + '-remove-' + n) || q(m[1] + '-' + (n - 1)) || q(pre + '-remove-' + (n - 1));
  }

  function renderCartDrawer(html) {
    if (!html) return;
    var current = $('[data-cart-drawer-inner]');
    if (!current) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-cart-drawer-inner]');
    if (!fresh) return;
    var hadFocus = current.contains(document.activeElement);
    var focusId = hadFocus && document.activeElement.getAttribute('data-focus-id');
    var scroller = $('.gh-drawer__body', current);
    var scrollTop = scroller ? scroller.scrollTop : 0;
    current.innerHTML = fresh.innerHTML;
    if (GH.updateShipDay) GH.updateShipDay(current);
    current.classList.remove('is-adding');
    clearTimeout(addingTimer);
    applyPendingSteps(current);
    var newScroller = $('.gh-drawer__body', current);
    if (newScroller) newScroller.scrollTop = scrollTop;
    if (hadFocus) {
      var again = findFocusTarget(current, focusId) || current;
      try { again.focus({ preventScroll: true }); } catch (e) { again.focus(); }
    }
    GH.carousels.update(current);
  }

  var cartRefreshSeq = 0;
  var cartSectionsUrl = function () {
    // Via the shop root: on /cart, Shopify answers a JSON Accept header with cart.js data instead
    // of the section HTML, which left the drawer showing the old (empty) cart.
    return (routes.root || '/') + '?sections=cart-drawer';
  };

  GH.cart = {
    get: function () {
      return jsonFetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
    },
    refresh: function () {
      // Several refreshes can overlap (our add + an app's add). Only the most recently started one
      // may render, so a slower, older response can't overwrite the drawer with a stale cart.
      var seq = ++cartRefreshSeq;
      var drawer = $('[data-cart-drawer-inner]');
      var sectionReq = drawer
        ? fetch(cartSectionsUrl(), { cache: 'no-store', ghInternal: true })
            .then(function (r) { return r.ok ? r.json() : {}; })
            .catch(function () { return {}; })
        : Promise.resolve({});
      return Promise.all([GH.cart.get(), sectionReq]).then(function (res) {
        var cart = res[0];
        GH.cart.last = cart;
        if (seq !== cartRefreshSeq) return cart;
        renderCartDrawer(res[1] && res[1]['cart-drawer']);
        updateCartCounts(cart.item_count);
        emit('gh:cart:updated', { cart: cart });
        return cart;
      });
    },
    add: function (items) {
      var body, headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (typeof FormData !== 'undefined' && items instanceof FormData) {
        body = items;
      } else {
        if (!Array.isArray(items)) items = [items];
        body = JSON.stringify({
          items: items.map(function (it) {
            var o = { id: Number(it.id), quantity: Number(it.quantity) || 1 };
            if (it.properties) o.properties = it.properties;
            if (it.selling_plan) o.selling_plan = it.selling_plan;
            return o;
          })
        });
        headers['Content-Type'] = 'application/json';
      }
      // Ask Shopify to render the drawer in the same response (bundled section rendering): one round trip.
      if (body instanceof FormData) { body.append('sections', 'cart-drawer'); body.append('sections_url', location.pathname); }
      else { var parsed = JSON.parse(body); parsed.sections = 'cart-drawer'; parsed.sections_url = location.pathname; body = JSON.stringify(parsed); }
      return jsonFetch(routes.cartAdd + '.js', { method: 'POST', headers: headers, body: body })
        .then(function (res) { return applyBundled(res && res.sections, null); })
        .then(function (cart) {
          if (GH.settings.openCartOnAdd && $('[data-drawer="cart"]')) GH.drawer.open('cart');
          else GH.toast(str('added'));
          return cart;
        })
        .catch(function (err) {
          GH.cart.clearAdding();
          GH.toast((err && err.message) || str('error'));
          throw err;
        });
    },
    // ref: 1-based line number, a line item key, or { key, line, variant }. Line keys change whenever
    // a quantity (and with it an automatic discount allocation) changes, so the line is resolved
    // against a fresh /cart.js right before sending, and changes run one at a time.
    change: function (ref, quantity) {
      var qty = Math.max(0, parseInt(quantity, 10) || 0);
      if (typeof ref === 'number') ref = { line: ref };
      else if (typeof ref === 'string') ref = { key: ref };
      ref = ref || {};
      function send(cart) {
        var line = resolveLine(cart, ref);
        if (!line || cart.items[line - 1].quantity === qty) return Promise.resolve(null); // gone already / nothing to do
        return jsonFetch(routes.cartChange + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: qty, sections: 'cart-drawer', sections_url: location.pathname })
        });
      }
      return cartQueue(function () {
        // Use the cart we already know (kept up to date by every render) instead of an extra /cart.js
        // round trip; if that turns out stale (line moved), fetch a fresh cart and try once more.
        var known = GH.cart.last ? Promise.resolve(GH.cart.last) : GH.cart.get();
        return known
          .then(send)
          .catch(function () { return GH.cart.get().then(send); })
          .then(function (res) { return res ? applyBundled(res.sections, res) : GH.cart.refresh(); })
          .catch(function (err) {
            GH.toast((err && err.message) || str('error'));
            return GH.cart.refresh().then(function () { throw err; });
          });
      });
    }
  };

  // Render the drawer HTML that came back with an add/change request. Falls back to a normal refresh
  // when it is missing or not this theme's drawer. cart: the cart JSON when the response includes it.
  function applyBundled(sections, cart) {
    var html = sections && sections['cart-drawer'];
    if (!html || html.indexOf('data-cart-drawer-inner') === -1) return GH.cart.refresh();
    ++cartRefreshSeq; // an older refresh still in flight must not overwrite this newer state
    renderCartDrawer(html);
    if (cart && cart.items) {
      GH.cart.last = cart;
      updateCartCounts(cart.item_count);
      emit('gh:cart:updated', { cart: cart, sections: sections });
      return Promise.resolve(cart);
    }
    return GH.cart.get().then(function (fresh) {
      GH.cart.last = fresh;
      updateCartCounts(fresh.item_count);
      emit('gh:cart:updated', { cart: fresh, sections: sections });
      return fresh;
    });
  }

  var cartChain = Promise.resolve();
  function cartQueue(task) {
    var run = cartChain.then(task, task);
    cartChain = run.catch(function () {});
    return run;
  }

  function resolveLine(cart, ref) {
    var items = (cart && cart.items) || [];
    var i;
    if (ref.key) for (i = 0; i < items.length; i++) if (items[i].key === ref.key) return i + 1;
    var v = ref.variant ? String(ref.variant) : (ref.key ? String(ref.key).split(':')[0] : '');
    var at = ref.line && items[ref.line - 1];
    if (at && (!v || String(at.variant_id) === v)) return ref.line;
    if (!v) return 0;
    var hits = [];
    for (i = 0; i < items.length; i++) if (String(items[i].variant_id) === v) hits.push(i + 1);
    return hits.length === 1 ? hits[0] : 0;
  }

  /* Drawer quantity steppers ([data-cart-step="1|-1"] inside a .gh-line[data-line]): clicks — mouse or
     keyboard — are counted locally and shown at once, then one debounced change sends the final
     quantity. Clicks made while that request runs are kept and sent afterwards. */
  var stepPending = [];
  function lineRef(el) {
    return {
      key: el.getAttribute('data-line-key'),
      line: parseInt(el.getAttribute('data-line'), 10) || 0,
      variant: el.getAttribute('data-variant') || ''
    };
  }
  function stepFor(ref) {
    return stepPending.filter(function (s) { return s.line === ref.line && s.variant === ref.variant; })[0];
  }
  // Minus button turns into a trash can at quantity 1 (cart drawer + cart page)
  GH.qtyMinusState = function (btn, qty) {
    if (!btn) return;
    var last = Number(qty) <= 1;
    if (!btn.hasAttribute('data-label-decrease')) btn.setAttribute('data-label-decrease', btn.getAttribute('aria-label') || '');
    btn.classList.toggle('is-last', last);
    btn.setAttribute('aria-label', last ? (btn.getAttribute('data-label-remove') || btn.getAttribute('data-label-decrease')) : btn.getAttribute('data-label-decrease'));
    var lineEl = btn.closest('.gh-line, [data-cart-line]');
    var rm = lineEl && lineEl.querySelector('[data-cart-remove-line], [data-cart-remove]');
    if (rm) rm.classList.toggle('is-qty-one', last);
  };
  function showStep(lineEl, qty) {
    var val = $('[data-cart-step-val]', lineEl);
    if (val) val.textContent = qty;
    GH.qtyMinusState($('[data-cart-step="-1"]', lineEl), qty);
    lineEl.classList.add('is-pending');
  }
  function applyPendingSteps(root) {
    stepPending.forEach(function (st) {
      if (!st.timer && st.qty === st.sent) return; // nothing newer than what the server just rendered
      var el = $$('.gh-line[data-line]', root).filter(function (l) {
        var r = lineRef(l);
        return r.line === st.line && r.variant === st.variant;
      })[0];
      if (el) showStep(el, st.qty);
    });
  }
  function flushStep(st) {
    st.timer = null;
    st.sent = st.qty;
    st.inflight = (st.inflight || 0) + 1;
    GH.cart.change({ key: st.key, line: st.line, variant: st.variant }, st.qty)
      .catch(function () { st.qty = st.sent; })
      .then(function () {
        st.inflight--;
        if (!st.timer && !st.inflight) {
          stepPending.splice(stepPending.indexOf(st), 1);
          var el = $$('.gh-line[data-line]').filter(function (l) { return l.getAttribute('data-line') === String(st.line); })[0];
          if (el) el.classList.remove('is-pending');
        }
      });
  }

  /* Open the cart drawer the moment an add-to-cart is clicked, with a placeholder line for the product,
     instead of waiting ~0.6 s for Shopify. The real drawer HTML replaces it as soon as it arrives. */
  var addingTimer = null;
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function textOf(root, sel) { var el = root && root.querySelector(sel); return el ? el.textContent.replace(/\s+/g, ' ').trim() : ''; }
  function imgOf(root, sel) { var el = root && root.querySelector(sel); return el ? (el.currentSrc || el.src || '') : ''; }
  function addMetaFrom(el) {
    var card = el.closest('[data-product-card], .gh-mini, .gh-mprod__item');
    if (card) return { title: textOf(card, '.gh-card__title, .gh-mini__title, .gh-mprod__title'), image: imgOf(card, 'img'), price: textOf(card, '.gh-card__price-now, .gh-mini__price, .gh-mprod__price') };
    var form = el.closest('form[data-product-form]');
    if (form) {
      var label = (form.querySelector('[data-pdp-atc]') || {}).textContent || '';
      return { title: textOf(document, '.gh-pdp__title'), image: imgOf(document, '.gh-pdp-gallery__slide img'), price: label.indexOf('·') > -1 ? label.split('·').pop().trim() : '' };
    }
    return {};
  }
  GH.cart.clearAdding = function () {
    clearTimeout(addingTimer);
    var inner = $('[data-cart-drawer-inner]');
    if (!inner) return;
    inner.classList.remove('is-adding');
    var ghost = $('.gh-line--ghost', inner);
    if (ghost) {
      ghost.remove();
      var items = $('.gh-line-items', inner), empty = $('[data-cart-empty]', inner);
      if (items && !$('.gh-line', items)) { items.hidden = true; if (empty) empty.hidden = false; }
    }
  };
  GH.cart.showAdding = function (meta) {
    var inner = $('[data-cart-drawer-inner]');
    if (!GH.settings.openCartOnAdd || !inner || !$('[data-drawer="cart"]')) return;
    if (!GH.drawer.isOpen || !GH.drawer.isOpen('cart')) GH.drawer.open('cart');
    inner.classList.add('is-adding');
    var items = $('.gh-line-items', inner);
    if (items && !$('.gh-line--ghost', items) && meta && meta.title) {
      var empty = $('[data-cart-empty]', inner);
      if (empty) empty.hidden = true;
      items.hidden = false;
      items.insertAdjacentHTML('afterbegin',
        '<div class="gh-line gh-line--ghost" aria-hidden="true">' +
          '<span class="gh-line__media gh-media">' + (meta.image ? '<img src="' + escapeHtml(meta.image) + '" alt="">' : '') + '</span>' +
          '<div class="gh-line__ghost-info"><span class="gh-line__title">' + escapeHtml(meta.title) + '</span>' +
          '<span class="gh-line__ghost-status">' + escapeHtml(str('adding')) + '</span>' +
          (meta.price ? '<span class="gh-line__ghost-price">' + escapeHtml(meta.price) + '</span>' : '') + '</div>' +
        '</div>');
      var body = $('.gh-drawer__body', inner); if (body) body.scrollTop = 0;
    }
    clearTimeout(addingTimer);
    addingTimer = setTimeout(GH.cart.clearAdding, 10000); // safety net if no response ever comes
  };

  function setPending(btn, pending) {
    if (!btn) return;
    btn.disabled = pending;
    btn.classList.toggle('is-loading', pending);
    if (pending) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-to-cart]');
    if (btn) {
      e.preventDefault();
      if (btn.disabled) return;
      var id = btn.getAttribute('data-add-to-cart');
      if (!id) return;
      setPending(btn, true);
      GH.cart.showAdding(addMetaFrom(btn));
      GH.cart.add([{ id: id, quantity: parseInt(btn.getAttribute('data-quantity'), 10) || 1 }])
        .catch(function () {})
        .then(function () { setPending(btn, false); });
      return;
    }
    var step = e.target.closest('[data-cart-step]');
    var stepLine = step && step.closest('.gh-line[data-line]');
    if (stepLine) {
      e.preventDefault();
      var ref = lineRef(stepLine);
      var st = stepFor(ref);
      var base = st ? st.qty : parseInt(stepLine.getAttribute('data-qty'), 10) || 0;
      var next = Math.max(0, base + (parseInt(step.getAttribute('data-cart-step'), 10) || 0));
      if (!st) { st = ref; stepPending.push(st); }
      st.key = ref.key;
      st.qty = next;
      showStep(stepLine, next);
      clearTimeout(st.timer);
      st.timer = setTimeout(function () { flushStep(st); }, 200);
      return;
    }
    var rm = e.target.closest('[data-cart-remove-line]');
    var rmLine = rm && rm.closest('.gh-line[data-line]');
    if (rmLine) {
      e.preventDefault();
      var rref = lineRef(rmLine);
      var pend = stepFor(rref);
      if (pend) {
        clearTimeout(pend.timer);
        pend.timer = null;
        pend.qty = pend.sent;
        if (!pend.inflight) stepPending.splice(stepPending.indexOf(pend), 1);
      }
      rmLine.classList.add('is-updating');
      GH.cart.change(rref, 0).catch(function () {});
      return;
    }
    var change = e.target.closest('[data-cart-change]');
    if (change && change.hasAttribute('data-quantity')) {
      e.preventDefault();
      var line = change.closest('.gh-line');
      if (line) line.classList.add('is-updating');
      GH.cart.change(change.getAttribute('data-cart-change'), change.getAttribute('data-quantity')).catch(function () {});
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-cart-qty-input]');
    if (input) GH.cart.change(input.getAttribute('data-cart-qty-input'), input.value).catch(function () {});
  });

  /* ------------------------------- empty cart drawer: search, popular terms */
  // An empty cart drawer ([data-cart-empty]) shows its own search field. It reuses the header's
  // predictive search section (products only) and its own results list. Escape clears a filled field
  // before the drawer closes; arrows move between the field and the results.
  var cdController = null;
  function cdParts(el) {
    var root = el && el.closest && el.closest('[data-cd-search]');
    if (!root) return null;
    return {
      root: root,
      input: $('[data-cd-search-input]', root),
      results: $('[data-cd-search-results]', root),
      clear: $('[data-cd-search-clear]', root),
      def: $('[data-cd-search-default]', root.parentNode)
    };
  }
  function cdShow(p, html) {
    p.results.classList.remove('is-loading');
    p.results.innerHTML = html || '';
    p.results.hidden = !p.results.innerHTML.trim();
    p.input.setAttribute('aria-expanded', p.results.hidden ? 'false' : 'true');
    if (p.def) p.def.hidden = !p.results.hidden || !$('[data-cd-term]', p.def);
  }
  function cdSearch(p, q) {
    if (!p || !p.input || !document.contains(p.input)) return;
    if (p.clear) p.clear.hidden = !q;
    if (cdController) cdController.abort();
    cdController = null;
    if (!q) { cdShow(p, ''); return; }
    cdController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var url = routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=5' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
      '&section_id=header-predictive-search';
    p.results.classList.add('is-loading');
    fetch(url, cdController ? { signal: cdController.signal } : {})
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        if (!document.contains(p.input) || p.input.value.trim() !== q) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var col = doc.querySelector('[data-ps-content] .gh-ps-col--products');
        cdShow(p, col ? col.innerHTML : '');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        cdShow(p, '');
      });
  }
  var cdDebounced = debounce(cdSearch, 220);
  function cdReset(p) {
    p.input.value = '';
    cdSearch(p, '');
  }
  // Popular terms: the drawer's own setting, or else the header search's terms.
  function cdFillTerms() {
    var box = $('[data-cart-empty] [data-cd-terms]');
    if (!box) return;
    if (!$('[data-cd-term]', box)) {
      var seen = {};
      $$('[data-search] [data-search-term]').forEach(function (src) {
        var term = (src.getAttribute('data-search-term') || '').trim();
        if (!term || seen[term]) return;
        seen[term] = true;
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'gh-chip';
        chip.setAttribute('data-cd-term', term);
        chip.textContent = term;
        box.appendChild(chip);
      });
    }
    var wrap = box.closest('[data-cd-search-default]');
    if (wrap && !$('[data-cd-term]', box)) wrap.hidden = true;
  }
  cdFillTerms();
  document.addEventListener('gh:cart:updated', cdFillTerms);
  document.addEventListener('gh:drawer:open', function (e) { if (e.detail && e.detail.name === 'cart') cdFillTerms(); });

  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-cd-search-input]');
    if (!input) return;
    var p = cdParts(input);
    var q = input.value.trim();
    if (p.clear) p.clear.hidden = !q;
    if (!q) cdSearch(p, '');
    cdDebounced(p, q);
  });
  document.addEventListener('search', function (e) {
    var input = e.target.closest && e.target.closest('[data-cd-search-input]');
    if (input && !input.value.trim()) cdSearch(cdParts(input), '');
  }, true);
  document.addEventListener('submit', function (e) {
    var p = cdParts(e.target);
    if (p && p.input && !p.input.value.trim()) { e.preventDefault(); p.input.focus(); }
  });
  document.addEventListener('click', function (e) {
    var clear = e.target.closest('[data-cd-search-clear]');
    if (clear) {
      e.preventDefault();
      var cp = cdParts(clear);
      cdReset(cp);
      cp.input.focus();
      return;
    }
    var term = e.target.closest('[data-cd-term]');
    var empty = term && term.closest('[data-cart-empty]');
    var tp = empty && cdParts($('[data-cd-search]', empty));
    if (!tp) return;
    e.preventDefault();
    tp.input.value = term.getAttribute('data-cd-term');
    cdSearch(tp, tp.input.value.trim());
    // keyboard or desktop: continue in the field; touch: don't pop the on-screen keyboard
    if (isDesktop() || e.detail === 0) tp.input.focus();
    else term.blur();
  });
  // Capture phase: runs before the drawer's own Escape handler.
  document.addEventListener('keydown', function (e) {
    var p = cdParts(e.target);
    if (!p || !p.input) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (!p.input.value) return; // empty field: let Escape close the drawer
      e.preventDefault();
      e.stopPropagation();
      cdReset(p);
      p.input.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var items = p.results.hidden ? [] : visibleFocusables(p.results);
    if (!items.length) return;
    if (e.target === p.input) {
      if (e.key !== 'ArrowDown') return;
      e.preventDefault();
      items[0].focus();
      return;
    }
    var idx = items.indexOf(document.activeElement);
    if (idx === -1) return;
    e.preventDefault();
    if (e.key === 'ArrowDown') items[Math.min(items.length - 1, idx + 1)].focus();
    else if (idx === 0) p.input.focus();
    else items[idx - 1].focus();
  }, true);

  document.addEventListener('click', function (e) {
    var atc = e.target.closest && e.target.closest('form[data-product-form] [type="submit"]');
    if (!atc || atc.disabled) return;
    GH.cart.showAdding(addMetaFrom(atc));
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[data-product-form]')) return;
    e.preventDefault();
    var submitter = e.submitter || $('[type="submit"]', form);
    setPending(submitter, true);
    GH.cart.add(new FormData(form))
      .catch(function () {})
      .then(function () { setPending(submitter, false); });
  });

  /* ------------------------------------------- cart changes made by apps */
  // Apps such as Kaching Bundles add to the cart with their own fetch/XHR calls. Watch for
  // successful /cart/add|change|update|clear requests we didn't make and re-sync the drawer.
  var appCartTimer = null;
  var appCartAdded = false;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function onAppCartWrite(url) {
    if (/\/cart\/add/.test(url)) appCartAdded = true;
    clearTimeout(appCartTimer);
    appCartTimer = setTimeout(function () {
      var opened = appCartAdded;
      appCartAdded = false;
      GH.cart.refresh().then(function () {
        if (!opened) return;
        if (GH.settings.openCartOnAdd && $('[data-drawer="cart"]')) GH.drawer.open('cart');
        else GH.toast(str('added'));
      }).catch(function () {});
    }, 150);
  }
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = String((input && input.url) || input || '');
      var p = nativeFetch.apply(this, arguments);
      if (CART_WRITE.test(url) && !(init && init.ghInternal)) {
        p.then(function (res) { if (res && res.ok) onAppCartWrite(url); }).catch(function () {});
      }
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      var u = String(url || '');
      if (CART_WRITE.test(u)) {
        this.addEventListener('load', function () { if (this.status >= 200 && this.status < 300) onAppCartWrite(u); });
      }
      return nativeOpen.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------- favourites */
  var FAV_KEY = 'gh:favorites';
  function favLoad() {
    try {
      var list = JSON.parse(storage.get(FAV_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (i) { return i && i.handle; }) : [];
    } catch (e) { return []; }
  }
  function favSave(list) {
    try { storage.set(FAV_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
  }

  GH.favorites = {
    get: favLoad,
    has: function (handle) { return favLoad().some(function (i) { return i.handle === handle; }); },
    toggle: function (btn) {
      var handle = btn.getAttribute('data-wish');
      var list = favLoad();
      var idx = -1;
      list.forEach(function (i, n) { if (i.handle === handle) idx = n; });
      if (idx > -1) list.splice(idx, 1);
      else list.unshift({
        handle: handle,
        title: btn.getAttribute('data-wish-title') || '',
        url: btn.getAttribute('data-wish-url') || '',
        image: btn.getAttribute('data-wish-image') || '',
        price: btn.getAttribute('data-wish-price') || '',
        compare: btn.getAttribute('data-wish-compare') || '',
        variant: btn.getAttribute('data-wish-variant') || ''
      });
      favSave(list);
      GH.favorites.sync();
      GH.favorites.render();
      emit('gh:favorites:updated', { favorites: list });
      return idx === -1;
    },
    remove: function (handle) {
      favSave(favLoad().filter(function (i) { return i.handle !== handle; }));
      GH.favorites.sync();
      GH.favorites.render();
    },
    sync: function (root) {
      var list = favLoad();
      var set = {};
      list.forEach(function (i) { set[i.handle] = true; });
      $$('[data-wish]', root).forEach(function (b) {
        b.setAttribute('aria-pressed', set[b.getAttribute('data-wish')] ? 'true' : 'false');
      });
      $$('[data-fav-count]').forEach(function (el) {
        el.textContent = list.length;
        if (el.hasAttribute('data-hide-zero')) el.hidden = !list.length;
      });
    },
    render: function () {
      var listEl = $('[data-fav-list]');
      if (!listEl) return;
      var tpl = $('[data-fav-template]');
      var items = favLoad();
      listEl.innerHTML = '';
      items.forEach(function (it) {
        if (!tpl || !tpl.content) return;
        var node = tpl.content.firstElementChild.cloneNode(true);
        var link = $('[data-fav-link]', node);
        link.href = it.url || '#';
        link.textContent = it.title;
        var media = $('[data-fav-media]', node);
        media.href = it.url || '#';
        var img = $('[data-fav-img]', node);
        if (it.image) { img.src = it.image; } else { img.remove(); }
        $('[data-fav-price]', node).textContent = it.price;
        $('[data-fav-compare]', node).textContent = it.compare;
        var add = $('[data-fav-add]', node);
        var view = $('[data-fav-view]', node);
        if (it.variant) {
          add.setAttribute('data-add-to-cart', it.variant);
          view.remove();
        } else {
          add.remove();
          view.href = it.url || '#';
        }
        $('[data-fav-remove]', node).setAttribute('data-fav-remove', it.handle);
        listEl.appendChild(node);
      });
      var empty = $('[data-fav-empty]');
      if (empty) empty.hidden = items.length > 0;
      var foot = $('[data-fav-foot]');
      if (foot) foot.hidden = !items.some(function (i) { return i.variant; });
    }
  };

  document.addEventListener('click', function (e) {
    var wish = e.target.closest('[data-wish]');
    if (wish) {
      e.preventDefault();
      GH.favorites.toggle(wish);
      return;
    }
    var rm = e.target.closest('[data-fav-remove]');
    if (rm) {
      e.preventDefault();
      // the list is re-rendered: keep focus on the same row position (next item), else a sensible fallback
      var listEl = rm.closest('[data-fav-list]');
      var index = listEl ? $$('[data-fav-remove]', listEl).indexOf(rm) : -1;
      GH.favorites.remove(rm.getAttribute('data-fav-remove'));
      if (listEl) {
        var rest = $$('[data-fav-remove]', listEl);
        var drawer = rm.closest && listEl.closest('[role="dialog"]');
        var next = rest[Math.min(index, rest.length - 1)] ||
          (drawer && $('[data-fav-empty]:not([hidden]) a, [data-fav-empty]:not([hidden]) button', drawer)) ||
          (drawer && $('[data-close]', drawer)) || drawer;
        if (next) next.focus();
      }
      return;
    }
    var all = e.target.closest('[data-fav-add-all]');
    if (all) {
      e.preventDefault();
      var items = favLoad().filter(function (i) { return i.variant; }).map(function (i) { return { id: i.variant, quantity: 1 }; });
      if (!items.length) { GH.toast(str('favNothing', str('error'))); return; }
      setPending(all, true);
      GH.cart.add(items).catch(function () {}).then(function () { setPending(all, false); });
    }
  });

  window.addEventListener('storage', function (e) {
    if (e.key === FAV_KEY) { GH.favorites.sync(); GH.favorites.render(); }
  });

  /* -------------------------------------------------------------- carousels */
  function carouselTrack(carousel) { return carousel && $('[data-carousel-track]', carousel); }
  function updateCarousel(carousel) {
    var track = carouselTrack(carousel);
    var prev = $('[data-carousel-prev]', carousel);
    var next = $('[data-carousel-next]', carousel);
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }
  GH.carousels = {
    update: function (root) {
      if (root && root.matches && root.matches('[data-carousel]')) updateCarousel(root);
      $$('[data-carousel]', root).forEach(updateCarousel);
    }
  };
  function carouselStep(track) {
    var cards = Array.prototype.filter.call(track.children, function (c) { return c.offsetWidth > 0; });
    if (!cards.length) return track.clientWidth;
    var style = getComputedStyle(track);
    var gap = parseFloat(style.columnGap || style.gap) || 0;
    var pad = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    var size = cards[0].getBoundingClientRect().width + gap;
    var perView = Math.max(1, Math.floor((track.clientWidth - pad + gap) / size));
    return perView * size;
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-carousel-prev], [data-carousel-next]');
    if (!btn) return;
    var carousel = btn.closest('[data-carousel]');
    var track = carouselTrack(carousel);
    if (!track) return;
    e.preventDefault();
    var dir = btn.hasAttribute('data-carousel-prev') ? -1 : 1;
    track.scrollBy({ left: dir * carouselStep(track), behavior: 'smooth' });
  });
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.nodeType === 1 && t.hasAttribute('data-carousel-track')) {
      var c = t.closest('[data-carousel]');
      if (c) {
        cancelAnimationFrame(t._ghRaf);
        t._ghRaf = requestAnimationFrame(function () { updateCarousel(c); });
      }
    }
  }, true);
  window.addEventListener('resize', debounce(function () { GH.carousels.update(); }, 150));

  // Sliders always start at the first card when a page loads. Safari restores an old scroll offset
  // (reload, back/forward cache) and re-snaps mandatory snap rows to a later card when images load
  // and card widths change. Until the visitor touches a row, any such offset is put back to 0.
  var SLIDER_SEL = '.gh-row, [data-carousel-track], .gh-ps-best, .gh-cartp__up-list';
  var sliderSettleUntil = 0;
  function markSliderTouched(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var row = t.closest(SLIDER_SEL);
    if (!row) {
      var c = t.closest('[data-carousel]');
      row = c && carouselTrack(c);
    }
    if (row) row._ghTouched = true;
  }
  ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach(function (type) {
    document.addEventListener(type, markSliderTouched, { capture: true, passive: true });
  });
  function resetSlider(el) {
    if (el._ghTouched || el.scrollLeft < 1) return;
    var behavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollLeft = 0;
    el.style.scrollBehavior = behavior;
  }
  function resetSliders(settleMs) {
    sliderSettleUntil = Math.max(sliderSettleUntil, Date.now() + settleMs);
    document.querySelectorAll(SLIDER_SEL).forEach(resetSlider);
  }
  // Late re-snaps (images, fonts, lazy sections) during the first moments after load
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (Date.now() > sliderSettleUntil || !t || t.nodeType !== 1 || !t.matches(SLIDER_SEL)) return;
    resetSlider(t);
  }, true);
  resetSliders(3000);
  window.addEventListener('load', function () { resetSliders(2000); });
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll(SLIDER_SEL).forEach(function (el) { el._ghTouched = false; });
    resetSliders(1500);
  });

  /* ----------------------------------------- shipping line: today / tomorrow */
  // snippets/ship-day.liquid renders both texts; show the right one for the current hour in the Netherlands
  // (pages can be cached, so the server's choice may be stale).
  function amsterdamHour() {
    try { return parseInt(new Intl.DateTimeFormat('nl-NL', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Europe/Amsterdam' }).format(new Date()), 10); }
    catch (e) { return new Date().getHours(); }
  }
  GH.updateShipDay = function (root) {
    var hour = amsterdamHour();
    $$('[data-shipday]', root || document).forEach(function (el) {
      var after = hour >= (parseInt(el.getAttribute('data-cutoff'), 10) || 22);
      var today = $('[data-shipday-today]', el), tomorrow = $('[data-shipday-tomorrow]', el);
      if (today) today.hidden = after;
      if (tomorrow) tomorrow.hidden = !after;
    });
  };
  GH.updateShipDay();
  setInterval(function () { GH.updateShipDay(); }, 60000);
  document.addEventListener('gh:cart:updated', function () { setTimeout(function () { GH.updateShipDay(); }, 0); });

  /* --------------------------------------------- focus ring: keyboard only */
  // Mouse/touch hides the focus ring (html.gh-pointer); Tab or arrow keys bring it back for keyboard users.
  document.documentElement.classList.add('gh-pointer'); // until the first Tab press
  document.addEventListener('pointerdown', function () { document.documentElement.classList.add('gh-pointer'); }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key.indexOf('Arrow') === 0) document.documentElement.classList.remove('gh-pointer');
  }, true);

  /* ------------------------------------------------ A–Z letter filter (menu) */
  // snippets/header-az-list.liquid: a key shows only the links starting with that letter; "all" resets.
  document.addEventListener('click', function (e) {
    var key = e.target.closest && e.target.closest('[data-maz-key]');
    if (!key) return;
    e.preventDefault();
    var wrap = key.closest('[data-maz]');
    var letter = key.getAttribute('data-maz-key');
    var same = key.getAttribute('aria-pressed') === 'true';
    if (same && letter !== 'all') letter = 'all'; // pressing the active letter again shows everything
    $$('[data-maz-key]', wrap).forEach(function (k) {
      k.setAttribute('aria-pressed', k.getAttribute('data-maz-key') === letter ? 'true' : 'false');
    });
    $$('[data-maz-letter]', wrap).forEach(function (a) {
      a.hidden = letter !== 'all' && a.getAttribute('data-maz-letter') !== letter;
    });
  });

  /* --------------------------------------------- accordions + qty steppers */
  document.addEventListener('click', function (e) {
    var acc = e.target.closest('button[data-accordion][aria-expanded]');
    if (acc) {
      acc.setAttribute('aria-expanded', acc.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      return;
    }
    var step = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (step) {
      var wrap = step.closest('[data-qty]');
      var input = wrap && $('input', wrap);
      if (!input) return;
      e.preventDefault();
      var min = input.min !== '' ? parseInt(input.min, 10) : 1;
      if (isNaN(min) || min < 1) min = 1;
      var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
      var val = parseInt(input.value, 10) || min;
      val += step.hasAttribute('data-qty-plus') ? 1 : -1;
      val = Math.min(max, Math.max(min, val));
      if (String(val) !== input.value) {
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.closest || !input.closest('[data-qty]') || input.tagName !== 'INPUT') return;
    var min = input.min !== '' ? parseInt(input.min, 10) : 1;
    if (isNaN(min) || min < 1) min = 1;
    var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < min) input.value = min;
    else if (v > max) input.value = max;
  });

  /* ----------------------------------------------- header: hide on scroll */
  var lastY = window.scrollY;
  function headerWrap() { return $('.gh-header-wrap'); }
  function showHeader() { var w = headerWrap(); if (w) w.classList.remove('is-hidden'); }
  function onScroll() {
    var wrap = headerWrap();
    var y = window.scrollY;
    var dy = y - lastY;
    lastY = y;
    if (!wrap || !$('[data-hide-on-scroll]', wrap)) return;
    var busy = openStack.length || $('.gh-search.is-open') || $('.gh-nav__item.is-open');
    if (y < 160 || busy) wrap.classList.remove('is-hidden');
    else if (dy > 4) wrap.classList.add('is-hidden');
    else if (dy < -4) wrap.classList.remove('is-hidden');
  }
  var scrollRaf;
  window.addEventListener('scroll', function () {
    cancelAnimationFrame(scrollRaf);
    scrollRaf = requestAnimationFrame(function () { onScroll(); megaFit(); });
  }, { passive: true });
  // Keyboard focus on a header control while the header is slid away: bring it back first,
  // so the focused element is visible and the page doesn't jump.
  document.addEventListener('focusin', function (e) {
    var wrap = headerWrap();
    if (!wrap || !wrap.classList.contains('is-hidden') || !wrap.contains(e.target)) return;
    // The browser scrolls the page to reveal the slid-away element; undo that, the header slides in instead.
    var y = lastY;
    showHeader();
    var keep = function () {
      if (Math.abs(window.scrollY - y) > 1) {
        try { window.scrollTo({ top: y, behavior: 'instant' }); } catch (err) { window.scrollTo(0, y); }
      }
    };
    keep();
    requestAnimationFrame(keep);
  });

  // html.gh-search-open / html.gh-mega-open let CSS hide floating UI (the scratch gift tab) under them.
  function shellFlags() {
    var html = document.documentElement;
    html.classList.toggle('gh-search-open', !!$('.gh-search.is-open'));
    html.classList.toggle('gh-mega-open', !!$('.gh-nav__item.is-open'));
  }

  /* ---------------------------------------------------------- mega menu */
  var megaTimer;
  var megaNoReopen = false; // set while focus is moved back to a top link programmatically (Escape)
  function closeMega(except) {
    $$('.gh-nav__item.is-open').forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var a = $('.gh-nav__link[aria-expanded]', item);
      if (a) a.setAttribute('aria-expanded', 'false');
    });
    shellFlags();
  }
  // Limit the open panel to the space below the header, so tall panels (brands) scroll instead of overflowing.
  function megaFit() {
    var item = $('.gh-nav__item.is-open');
    var panel = item && $('.gh-mega', item);
    if (!panel) return;
    var top = panel.getBoundingClientRect().top;
    panel.style.setProperty('--gh-mega-max', Math.max(160, Math.floor(window.innerHeight - Math.max(0, top))) + 'px');
  }
  function openMega(item) {
    if (!item || item.classList.contains('is-open')) return;
    closeMega(item);
    closeSearch();
    item.classList.add('is-open');
    var a = $('.gh-nav__link[aria-expanded]', item);
    if (a) a.setAttribute('aria-expanded', 'true');
    megaFit();
    shellFlags();
  }
  window.addEventListener('resize', debounce(megaFit, 100));
  var canHover = window.matchMedia ? window.matchMedia('(hover: hover)') : { matches: true };
  document.addEventListener('mouseover', function (e) {
    if (!canHover.matches || !e.target.closest) return;
    var item = e.target.closest('.gh-nav__item');
    if (item && item.hasAttribute('data-mega')) {
      clearTimeout(megaTimer);
      if (!item.classList.contains('is-open')) {
        var anyOpen = !!$('.gh-nav__item.is-open');
        megaTimer = setTimeout(function () { openMega(item); }, anyOpen ? 0 : 90);
      }
      return;
    }
    // Anywhere else (a plain nav item, the logo, the search, the icons, outside the header): close.
    // A short delay inside the header forgives a pointer briefly crossing a gap.
    clearTimeout(megaTimer);
    if ($('.gh-nav__item.is-open')) megaTimer = setTimeout(function () { closeMega(); }, e.target.closest('[data-header]') ? 60 : 120);
  });
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.gh-nav__item[data-mega] > .gh-nav__link');
    if (link && !canHover.matches) {
      var item = link.parentNode;
      if (!item.classList.contains('is-open')) { e.preventDefault(); openMega(item); }
      return;
    }
    if (!e.target.closest('[data-nav]')) closeMega();
  });
  document.addEventListener('focusin', function (e) {
    var item = e.target.closest && e.target.closest('.gh-nav__item');
    if (item && item.hasAttribute('data-mega')) {
      if (!megaNoReopen) openMega(item);
      else if (!item.classList.contains('is-open')) closeMega();
    } else {
      // focus moved to a plain nav item, the aside links or out of the nav (Tab out of the panel)
      closeMega();
    }
    // search: close when focus leaves it
    var search = $('.gh-search.is-open');
    if (search && !search.contains(e.target)) closeSearch();
  });
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !openStack.length) {
      var open = $('.gh-nav__item.is-open');
      if (open) {
        closeMega();
        var a = $('.gh-nav__link', open);
        if (a) {
          megaNoReopen = true;
          try { a.focus(); } finally { megaNoReopen = false; }
        }
      }
      var s = $('.gh-search.is-open');
      if (s) {
        var input = $('[data-search-input]', s);
        var inPanel = s.contains(document.activeElement) && document.activeElement !== input;
        e.preventDefault(); // first Escape closes the panel (Chrome would also clear the field)
        closeSearch();
        if (input && inPanel) {
          // focus was on a result link inside the panel that is now hidden: back to the field
          searchNoOpen = true;
          try { input.focus(); } finally { searchNoOpen = false; }
        } else if (input && document.activeElement === input && !isDesktop()) {
          input.blur(); // mobile: dismiss the keyboard with the full-screen panel
        }
      }
    }
  });

  /* ------------------------------------------- announcement bar: rotate */
  // [data-announce] bars show one [data-announce-slide] at a time. Auto-advances every data-interval ms,
  // pauses while hovered/focused (or a slide is selected in the editor) and never auto-advances
  // with prefers-reduced-motion. State lives on the element, so re-rendered sections start fresh.
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  function announceState(bar) {
    if (!bar._ghAnnounce) bar._ghAnnounce = { index: 0, timer: null, hover: false, focus: false, editor: false };
    return bar._ghAnnounce;
  }
  function announceGo(bar, index) {
    var slides = $$('[data-announce-slide]', bar);
    if (slides.length < 2) return;
    var st = announceState(bar);
    var next = (index + slides.length) % slides.length;
    if (next === st.index && slides[next].classList.contains('is-active')) return;
    slides.forEach(function (s, i) {
      var active = i === next;
      s.classList.toggle('is-leaving', !active && s.classList.contains('is-active'));
      s.classList.toggle('is-active', active);
    });
    st.index = next;
  }
  function announceSchedule(bar) {
    var st = announceState(bar);
    clearTimeout(st.timer);
    if (st.hover || st.focus || st.editor || reduceMotion.matches || document.hidden) return;
    var ms = Math.max(3000, parseInt(bar.getAttribute('data-interval'), 10) || 5000);
    st.timer = setTimeout(function () {
      if (!document.documentElement.contains(bar)) return;
      announceGo(bar, st.index + 1);
      announceSchedule(bar);
    }, ms);
  }
  function announceInit(root) {
    $$('[data-announce]', root).forEach(function (bar) {
      if (bar._ghAnnounce) return;
      var slides = $$('[data-announce-slide]', bar);
      var st = announceState(bar);
      slides.forEach(function (s, i) { if (s.classList.contains('is-active')) st.index = i; });
      announceSchedule(bar);
    });
  }
  function announceAll() { $$('[data-announce]').forEach(announceSchedule); }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-announce-prev], [data-announce-next]');
    if (!btn) return;
    var bar = btn.closest('[data-announce]');
    if (!bar) return;
    var st = announceState(bar);
    announceGo(bar, st.index + (btn.hasAttribute('data-announce-prev') ? -1 : 1));
    var track = $('[data-announce-track]', bar);
    if (track) track.setAttribute('aria-live', 'polite');
    announceSchedule(bar);
  });
  document.addEventListener('mouseover', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announce]');
    if (!bar) return;
    var st = announceState(bar);
    if (!st.hover) { st.hover = true; clearTimeout(st.timer); }
  });
  document.addEventListener('mouseout', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announce]');
    if (!bar || (e.relatedTarget && bar.contains(e.relatedTarget))) return;
    announceState(bar).hover = false;
    announceSchedule(bar);
  });
  document.addEventListener('focusin', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announce]');
    if (!bar) return;
    var st = announceState(bar);
    st.focus = true;
    clearTimeout(st.timer);
  });
  document.addEventListener('focusout', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announce]');
    if (!bar || (e.relatedTarget && bar.contains(e.relatedTarget))) return;
    announceState(bar).focus = false;
    announceSchedule(bar);
  });
  document.addEventListener('visibilitychange', announceAll);
  if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', announceAll);
  else if (reduceMotion.addListener) reduceMotion.addListener(announceAll);

  /* ------------------------------------------------- search + predictive */
  var psController;
  var searchNoOpen = false; // set while focus is moved back to the field programmatically (Escape)
  function closeSearch() {
    $$('.gh-search.is-open').forEach(function (s) {
      s.classList.remove('is-open');
      var input = $('[data-search-input]', s);
      if (input) input.setAttribute('aria-expanded', 'false');
    });
    if (searchLock) { searchLock = false; lockBody(); }
    shellFlags();
  }
  function openSearch(search) {
    if (!search || searchNoOpen) return;
    closeMega();
    search.classList.add('is-open');
    var input = $('[data-search-input]', search);
    if (input) input.setAttribute('aria-expanded', 'true');
    // mobile: the panel + backdrop cover the page, so stop the page behind it from scrolling
    if (!isDesktop() && !searchLock) { searchLock = true; lockBody(); }
    showHeader();
    shellFlags();
  }
  // Focusable items in the open search panel, in visual order (pills, product links, results)
  function searchItems(search) {
    var card = $('.gh-search__card', search);
    return card ? visibleFocusables(card).filter(function (el) { return !el.closest('[hidden]'); }) : [];
  }
  function searchUI(search, q) {
    var results = $('[data-ps-results]', search);
    var def = $('[data-ps-default]', search);
    var clear = $('[data-search-clear]', search);
    if (clear) clear.hidden = !q;
    if (!q) {
      if (psController) psController.abort();
      if (results) { results.hidden = true; results.innerHTML = ''; }
      if (def) def.hidden = false;
      return;
    }
    fetchPredictive(search, q);
  }
  function fetchPredictive(search, q) {
    var results = $('[data-ps-results]', search);
    var def = $('[data-ps-default]', search);
    if (!results) return;
    if (psController) psController.abort();
    psController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var url = routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection,query&resources[limit]=4&resources[limit_scope]=each' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
      '&section_id=header-predictive-search';
    var inner = $('.gh-ps-results', results);
    if (inner) inner.classList.add('is-loading');
    fetch(url, psController ? { signal: psController.signal } : {})
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var input = $('[data-search-input]', search);
        if (input && input.value.trim() !== q) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-ps-content]');
        results.innerHTML = content ? content.innerHTML : '';
        results.hidden = !results.innerHTML.trim();
        if (def) def.hidden = !results.hidden;
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        results.hidden = true;
        if (def) def.hidden = false;
      });
  }
  var debouncedSearch = debounce(function (search, q) { searchUI(search, q); }, 200);

  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-search-input]');
    if (!input) return;
    var search = input.closest('[data-search]');
    openSearch(search);
    var q = input.value.trim();
    var clear = $('[data-search-clear]', search);
    if (clear) clear.hidden = !q;
    if (!q) searchUI(search, ''); // emptied: drop the old results right away
    debouncedSearch(search, q);
  });
  // type=search: Chrome clears the field on Escape / the native x and fires 'search' (not always 'input')
  document.addEventListener('search', function (e) {
    var input = e.target.closest && e.target.closest('[data-search-input]');
    if (input && !input.value.trim()) searchUI(input.closest('[data-search]'), '');
  }, true);
  document.addEventListener('focusin', function (e) {
    var input = e.target.closest && e.target.closest('[data-search-input]');
    if (input) openSearch(input.closest('[data-search]'));
  });
  // Arrow keys: from the field into the panel and through its links/buttons; ArrowUp on the first goes back.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    var search = e.target.closest && e.target.closest('.gh-search.is-open');
    if (!search) return;
    var input = $('[data-search-input]', search);
    var items = searchItems(search);
    if (!items.length) return;
    var idx = items.indexOf(document.activeElement);
    if (document.activeElement === input) {
      if (e.key !== 'ArrowDown') return;
      e.preventDefault();
      items[0].focus();
      return;
    }
    if (idx === -1) return;
    e.preventDefault();
    if (e.key === 'ArrowDown') items[Math.min(items.length - 1, idx + 1)].focus();
    else if (idx === 0 && input) input.focus();
    else items[idx - 1].focus();
  });
  document.addEventListener('click', function (e) {
    var search = e.target.closest('[data-search]');
    if (!search) { closeSearch(); return; }
    var input = $('[data-search-input]', search);
    if (e.target.closest('[data-search-cancel]')) {
      e.preventDefault();
      closeSearch();
      if (input) input.blur();
      return;
    }
    if (e.target.closest('[data-search-clear]')) {
      e.preventDefault();
      input.value = '';
      searchUI(search, '');
      input.focus();
      return;
    }
    var term = e.target.closest('[data-search-term]');
    if (term) {
      e.preventDefault();
      input.value = term.getAttribute('data-search-term');
      input.focus();
      searchUI(search, input.value.trim());
    }
  });

  /* ------------------------------------------------------------ mobile menu */
  var menuNextView = 'cats';
  function menuShow(view, dir) {
    var menu = $('[data-mmenu]');
    if (!menu) return;
    var target = $('[data-mmenu-view="' + view + '"]', menu);
    if (!target) { view = 'cats'; target = $('[data-mmenu-view="cats"]', menu); }
    if (!target) return;
    $$('[data-mmenu-view]', menu).forEach(function (v) {
      v.hidden = v !== target;
      v.classList.remove('is-fwd', 'is-back');
    });
    if (dir) {
      void target.offsetWidth;
      target.classList.add(dir === 'back' ? 'is-back' : 'is-fwd');
    }
    // "Menu-afbeelding bovenaan" tiles: only on the root views, not in a category drill-down
    var topTiles = $('[data-mmenu-top]', menu);
    if (topTiles) topTiles.hidden = view !== 'cats' && view !== 'brands';
    var tab = view === 'brands' ? 'brands' : 'cats';
    $$('[data-mmenu-tab]', menu).forEach(function (t) {
      var on = t.getAttribute('data-mmenu-tab') === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    // Switching views: make sure the start of the new view is in sight (the sticky tabs stay on top).
    var body = $('[data-mmenu-body]', menu);
    var tabs = $('.gh-mmenu__tabs', menu);
    if (body && dir) {
      var top = target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop -
        (tabs && !tabs.hidden ? tabs.offsetHeight : 0);
      if (/^cat-/.test(view)) body.scrollTop = Math.max(0, top);
      else if (body.scrollTop > top) body.scrollTop = Math.max(0, top);
    }
    return target;
  }
  var menuReturn = null; // the category button that opened the current drill-down
  function focusQuiet(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }
  document.addEventListener('gh:drawer:open', function (e) {
    if (e.detail.name === 'menu') {
      menuShow(menuNextView || 'cats', null);
      menuNextView = 'cats';
    }
  });
  document.addEventListener('click', function (e) {
    var go = e.target.closest('[data-mmenu-go]');
    if (go) {
      e.preventDefault();
      var from = go.closest('[data-mmenu-view]');
      var view = go.getAttribute('data-mmenu-go');
      var shown = menuShow(view, go.getAttribute('data-dir') || 'fwd');
      if (!shown) return;
      // Keep focus in the menu: the clicked control may just have been hidden with its view.
      if (/^cat-/.test(view)) {
        menuReturn = go;
        focusQuiet($('.gh-mmenu__back', shown) || shown);
      } else if (from && from.hidden) {
        var back = menuReturn && document.contains(menuReturn) && !menuReturn.closest('[hidden]') ? menuReturn : null;
        var menuEl = $('[data-mmenu]');
        back = back || $('[data-mmenu-tab][aria-selected="true"]', menuEl) || visibleFocusables(shown)[0];
        if (back) back.focus(); // may scroll the category row into view
        menuReturn = null;
      }
      return;
    }
    var letter = e.target.closest('[data-letter-btn]');
    if (letter && !letter.disabled) {
      var root = letter.closest('[data-mmenu-view]') || document;
      var l = letter.getAttribute('data-letter-btn');
      $$('[data-letter-btn]', root).forEach(function (b) { b.setAttribute('aria-pressed', b === letter ? 'true' : 'false'); });
      $$('[data-brand-letter]', root).forEach(function (a) { a.hidden = a.getAttribute('data-brand-letter') !== l; });
    }
  });

  /* -------------------------------------------- contact + withdrawal forms */
  /* Errors. Forms with a [data-form-error] box (the modals) show one message there; other forms
     (/pages/contact) get an inline .gh-field__error under each invalid field. Invalid fields get
     aria-invalid + aria-describedby pointing at their message; editing a field clears its error. */
  var errSeq = 0;
  function describe(field, id, on) {
    if (!id) return;
    var list = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return x && x !== id; });
    if (on) list.push(id);
    if (list.length) field.setAttribute('aria-describedby', list.join(' '));
    else field.removeAttribute('aria-describedby');
  }
  function inlineError(field, create) {
    var wrap = field.closest('.gh-field') || field.parentNode;
    var el = $('.gh-field__error', wrap);
    if (!el && create) {
      el = document.createElement('span');
      el.className = 'gh-field__error';
      el.id = (field.id || 'GhField' + (++errSeq)) + '-error';
      wrap.appendChild(el);
    }
    return el;
  }
  function clearFieldError(form, field) {
    if (!field || !field.removeAttribute) return;
    field.removeAttribute('aria-invalid');
    var box = form && $('[data-form-error]', form);
    if (box && field._ghBoxError) { box.textContent = ''; box.hidden = true; }
    field._ghBoxError = false;
    if (box) describe(field, box.id, false);
    var el = inlineError(field, false);
    if (el) { el.textContent = ''; el.hidden = true; describe(field, el.id, false); }
  }
  // problems = [{ field, message }]; an empty list clears every error. The first field gets focus.
  function fieldErrors(form, problems) {
    $$('[aria-invalid]', form).forEach(function (el) { clearFieldError(form, el); });
    var box = $('[data-form-error]', form);
    if (box) { box.textContent = ''; box.hidden = true; }
    if (!problems.length) return;
    problems.forEach(function (p, i) {
      if (box && i > 0) return; // the box shows one message at a time
      if (box) { box.textContent = p.message || str('error'); box.hidden = false; }
      var f = p.field;
      if (!f || !f.setAttribute) return;
      f.setAttribute('aria-invalid', 'true');
      if (box) {
        if (!box.id) box.id = (form.id || 'GhForm' + (++errSeq)) + '-error';
        describe(f, box.id, true);
        f._ghBoxError = true;
      } else {
        var el = inlineError(f, true);
        el.setAttribute('role', 'alert');
        el.textContent = p.message || str('error');
        el.hidden = false;
        describe(f, el.id, true);
      }
    });
    var first = problems[0].field;
    if (first && first.focus) first.focus();
  }
  function fieldError(form, message, field) {
    fieldErrors(form, message ? [{ field: field, message: message }] : []);
  }
  function checkField(form, f) {
    var rule = f.getAttribute('data-validate');
    var message = f.getAttribute('data-error') || str('error');
    if (rule === 'wd-items') { // withdrawal: at least one product (checked, picked or described)
      var wd = withdrawalPrepare(form);
      return wd.count ? null : { field: wd.target, message: message };
    }
    if (f.disabled) return null;
    var v = (f.value || '').trim();
    var bad = (rule === 'required' && !v) ||
      (rule === 'email' && !EMAIL_RE.test(v)) ||
      (rule === 'min10' && v.length < 10) ||
      (rule === 'pattern' && v && f.pattern && !new RegExp('^(?:' + f.pattern + ')$').test(v));
    return bad ? { field: f, message: message } : null;
  }
  // In DOM order (the withdrawal order is: order, name, email, products, reason).
  // all = true: every problem; otherwise the first one (or null).
  function validateFields(form, all) {
    var out = [];
    var fields = $$('[data-validate]', form);
    for (var i = 0; i < fields.length; i++) {
      var p = checkField(form, fields[i]);
      if (!p) continue;
      if (!all) return p;
      out.push(p);
    }
    return all ? out : null;
  }
  // Editing a field clears its error (and the shared box when that showed this field's message)
  function onFieldEdit(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var form = t.form || t.closest('form');
    if (!form) return;
    var group = t.closest('[data-validate="wd-items"]');
    if (group) $$('[aria-invalid]', group).forEach(function (el) { clearFieldError(form, el); });
    if (t.getAttribute('aria-invalid') === 'true') clearFieldError(form, t);
  }
  document.addEventListener('input', onFieldEdit);
  document.addEventListener('change', onFieldEdit);

  /* Posting. Shopify protects contact/customer forms with hCaptcha: its storefront script binds the
     form when it gets focus and then takes over the submit (solve, then a normal POST). So:
     - our validation runs first, in the capture phase (below), so the captcha never starts on bad input;
     - when the captcha has bound the form, Shopify posts it natively and we only remember what was sent
       (sessionStorage), so the modal / scratch card can show the right state after the redirect back;
     - otherwise we post with fetch; a challenge (redirect to /challenge, or a 403/429 "Verifying your
       connection…" page) or an answer we can't read falls back to a native submit. */
  var CHALLENGE_RE = /Verifying your connection|hcaptcha|h-captcha|challenge/i;
  function captchaManaged(form) {
    return !!(form.dataset && (form.dataset.hcaptchaBound || form.dataset.recaptchaBound));
  }
  // Result: { ok: true, doc } | 'challenge' | { error }
  function postForm(form) {
    return fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    }).then(function (res) {
      var url = res.url || '';
      if (/\/challenge/.test(url) || !res.ok) return 'challenge';
      return res.text().then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var same = form.id && doc.getElementById(form.id);
        if (/(contact|customer)_posted=true/.test(url) || (same && same.querySelector('[data-form-posted]'))) {
          return { ok: true, doc: doc };
        }
        if (!same && CHALLENGE_RE.test(html)) return 'challenge';
        var err = same && same.querySelector('[data-form-error], [data-sc-error]');
        var msg = err && err.textContent.trim();
        return msg ? { error: msg } : 'challenge';
      });
    });
  }
  var POSTED_KEY = 'gh:posted';
  function stashPosted(form, values) {
    try { window.sessionStorage.setItem(POSTED_KEY, JSON.stringify({ id: form.id, t: Date.now(), values: values || {} })); } catch (e) { /* ignore */ }
  }
  function takePosted() {
    try {
      var s = JSON.parse(window.sessionStorage.getItem(POSTED_KEY) || 'null');
      window.sessionStorage.removeItem(POSTED_KEY);
      return s && Date.now() - s.t < 30 * 60 * 1000 ? s : null;
    } catch (e) { return null; }
  }
  function nativeSubmit(form, values) {
    stashPosted(form, values);
    form._ghNative = true;
    // Once bound, hCaptcha replaces form.submit with "solve, then submit"; otherwise the plain submit.
    if (typeof form.submit === 'function') form.submit();
    else HTMLFormElement.prototype.submit.call(form);
  }
  // While Shopify's captcha handles the post, show the button as busy; release it if nothing happens
  // (challenge dismissed), so the form stays usable.
  function pendingWhileNative(btn) {
    setPending(btn, true);
    clearTimeout(btn && btn._ghNativeTimer);
    if (btn) btn._ghNativeTimer = setTimeout(function () { setPending(btn, false); }, 8000);
  }
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    $$('[aria-busy="true"]').forEach(function (b) { setPending(b, false); });
  });

  function formSubmitButton(form) {
    var btn = form.id ? $('[type="submit"][form="' + form.id + '"]') : null;
    return btn || $('[type="submit"]', form);
  }

  // Validation + preparation, before Shopify's captcha listeners (capture phase on window).
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || form._ghNative) return;
    var kind = form.getAttribute('data-ajax-form');
    var page = form.hasAttribute('data-validate-form');
    if (!kind && !page) return;
    var problems = validateFields(form, true);
    if (problems.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      fieldErrors(form, page ? problems : problems.slice(0, 1));
      return;
    }
    fieldErrors(form, []);
    if (kind) form._ghValues = prepareForm(form, kind);
  }, true);
  function showFormSuccess(form, values) {
    var modal = form.closest('[data-drawer]');
    var fields = $('[data-form-fields]', form);
    var success = $('[data-form-success]', form);
    if (fields) fields.hidden = true;
    if (success) {
      success.hidden = false;
      var text = $('[data-success-text]', success);
      // unknown placeholders (e.g. after a native post without stashed values) are dropped
      if (text) text.textContent = fill(text.getAttribute('data-success-text'), values || {}).replace(/\s*\{\w+\}/g, '');
      var ref = $('[data-success-ref]', success);
      if (ref) {
        ref.textContent = fill(ref.getAttribute('data-success-ref'), values || {});
        ref.hidden = !values || !values.ref;
      }
    }
    if (modal) {
      var a = $('[data-form-actions]', modal), d = $('[data-form-done-actions]', modal);
      if (a) a.hidden = true;
      if (d) d.hidden = false;
      var focusTarget = d && $('button', d);
      if (focusTarget) focusTarget.focus();
    }
  }
  function resetFormView(modal) {
    var form = $('form[data-ajax-form]', modal);
    if (!form) return;
    var fields = $('[data-form-fields]', form);
    var success = $('[data-form-success]', form);
    if (!success || success.hidden) return;
    if (fields) fields.hidden = false;
    success.hidden = true;
    var a = $('[data-form-actions]', modal), d = $('[data-form-done-actions]', modal);
    if (a) a.hidden = false;
    if (d) d.hidden = true;
    form.reset();
    $$('[data-wd-item]', form).forEach(function (c) { c.checked = false; });
    var picked = $('[data-wd-picked]', form);
    if (picked) picked.innerHTML = '';
  }
  document.addEventListener('gh:drawer:open', function (e) {
    if (e.detail.name === 'contact' || e.detail.name === 'withdrawal' || e.detail.name === 'advice') {
      var modal = overlay(e.detail.name);
      if (!modal) return;
      if (!modal._ghKeepSuccess) resetFormView(modal);
      // a re-opened modal starts without the errors of last time (unless it re-opens to show a server error)
      var form = $('form[data-ajax-form]', modal);
      if (form && !modal._ghKeepError) fieldErrors(form, []);
      modal._ghKeepSuccess = false;
      modal._ghKeepError = false;
    }
  });

  /* Withdrawal form, guests: search the catalogue (predictive search) and pick products.
     Picked products are rendered as checked [data-wd-item] rows, so withdrawalPrepare counts them. */
  var wdTimer = null;
  var wdSeq = 0;
  function wdEscape(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function wdClose(wrap) {
    var list = $('[data-wd-results]', wrap), input = $('[data-wd-search-input]', wrap);
    if (list) { list.hidden = true; list.innerHTML = ''; }
    if (input) { input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); }
  }
  function wdAdd(form, title, image, price) {
    var box = $('[data-wd-picked]', form);
    if (!box || !title) return;
    var exists = $$('[data-wd-item]', box).some(function (c) { return c.value === title; });
    if (exists) return;
    var label = document.createElement('label');
    label.className = 'gh-check-item';
    label.innerHTML =
      '<input type="checkbox" checked value="' + wdEscape(title) + '" data-wd-item>' +
      '<span class="gh-checkbox"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12l5 5 9-10"></path></svg></span>' +
      '<span class="gh-check-item__media gh-media">' + (image ? '<img src="' + wdEscape(image) + '" alt="" width="48" height="48" loading="lazy">' : '') + '</span>' +
      '<span class="gh-check-item__text"><span class="gh-check-item__title">' + wdEscape(title) + '</span></span>' +
      '<span class="gh-check-item__price">' + (price ? wdEscape(price) : '') + '</span>';
    box.appendChild(label);
    fieldError(form, '');
  }
  function wdRender(wrap, input, products, term) {
    var list = $('[data-wd-results]', wrap);
    if (!list) return;
    var html = products.map(function (p, i) {
      var img = p.image ? p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=96' : '';
      return '<li role="option" id="WdOpt' + i + '" class="gh-wd-search__opt" data-wd-opt data-title="' + wdEscape(p.title) + '" data-image="' + wdEscape(img) + '" data-price="' + wdEscape(p.price_label || '') + '">' +
        '<span class="gh-wd-search__img gh-media">' + (img ? '<img src="' + wdEscape(img) + '" alt="" width="40" height="40" loading="lazy">' : '') + '</span>' +
        '<span class="gh-wd-search__title">' + wdEscape(p.title) + '</span></li>';
    }).join('');
    var addCustom = fill(input.getAttribute('data-add-custom') || '"{term}"', { term: term });
    if (!products.length) {
      html = '<li class="gh-wd-search__empty">' + wdEscape(fill(input.getAttribute('data-no-results') || '', { term: term })) + '</li>';
    }
    html += '<li role="option" id="WdOptCustom" class="gh-wd-search__opt gh-wd-search__opt--custom" data-wd-opt data-title="' + wdEscape(term) + '">' + wdEscape(addCustom) + '</li>';
    list.innerHTML = html;
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }
  function wdSearch(wrap, input) {
    var term = input.value.trim();
    if (term.length < 2) { wdClose(wrap); return; }
    var seq = ++wdSeq;
    var url = (routes.predictiveSearch || '/search/suggest') + '.json?q=' + encodeURIComponent(term) +
      '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (data) {
        if (seq !== wdSeq) return;
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        wdRender(wrap, input, products.map(function (p) {
          return { title: p.title, image: p.image || (p.featured_image && p.featured_image.url), price_label: '' };
        }), term);
      })
      .catch(function () { if (seq === wdSeq) wdRender(wrap, input, [], term); });
  }
  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-wd-search-input]');
    if (!input) return;
    var wrap = input.closest('[data-wd-search]');
    clearTimeout(wdTimer);
    wdTimer = setTimeout(function () { wdSearch(wrap, input); }, 220);
  });
  function wdPick(opt) {
    var wrap = opt.closest('[data-wd-search]');
    var form = opt.closest('form');
    var input = $('[data-wd-search-input]', wrap);
    wdAdd(form, opt.getAttribute('data-title'), opt.getAttribute('data-image'), opt.getAttribute('data-price'));
    if (input) { input.value = ''; input.focus(); }
    wdClose(wrap);
  }
  document.addEventListener('click', function (e) {
    var opt = e.target.closest && e.target.closest('[data-wd-opt]');
    if (opt) { e.preventDefault(); wdPick(opt); return; }
    $$('[data-wd-search]').forEach(function (wrap) { if (!wrap.contains(e.target)) wdClose(wrap); });
  });
  document.addEventListener('keydown', function (e) {
    var input = e.target.closest && e.target.closest('[data-wd-search-input]');
    if (!input) return;
    var wrap = input.closest('[data-wd-search]');
    var opts = $$('[data-wd-opt]', wrap);
    var current = opts.findIndex(function (o) { return o.classList.contains('is-active'); });
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!opts.length) return;
      e.preventDefault();
      var next = e.key === 'ArrowDown' ? (current + 1) % opts.length : (current - 1 + opts.length) % opts.length;
      opts.forEach(function (o) { o.classList.remove('is-active'); o.removeAttribute('aria-selected'); });
      opts[next].classList.add('is-active');
      opts[next].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opts[next].id);
    } else if (e.key === 'Enter') {
      e.preventDefault(); // never submit the whole form from the search field
      if (current > -1) { wdPick(opts[current]); return; }
      var term = input.value.trim();
      if (term) { wdAdd(input.closest('form'), term, '', ''); input.value = ''; wdClose(wrap); }
    } else if (e.key === 'Escape' && !$('[data-wd-results]', wrap).hidden) {
      e.stopPropagation();
      wdClose(wrap);
    }
  }, true);
  // Unticking a picked (searched) product removes it from the list; focus moves to a neighbour
  // (or back to the search field) instead of dropping to <body>.
  document.addEventListener('change', function (e) {
    var c = e.target.closest && e.target.closest('[data-wd-picked] [data-wd-item]');
    if (!c || c.checked) return;
    var row = c.closest('.gh-check-item');
    var box = row.parentNode;
    var rows = $$('.gh-check-item', box);
    var i = rows.indexOf(row);
    var form = c.form || c.closest('form');
    row.remove();
    var next = rows[i + 1] || rows[i - 1];
    var target = next ? $('[data-wd-item]', next) : form && $('[data-wd-search-input]', form);
    if (target) target.focus();
  });

  function withdrawalPrepare(form) {
    var checks = $$('[data-wd-item]', form);
    var text = $('[data-wd-items-text]', form);
    var picked = checks.filter(function (c) { return c.checked; }).map(function (c) { return c.value; });
    var count = picked.length;
    if (text) {
      var t = text.value.trim();
      picked = t ? [t] : [];
      count = t ? Math.max(1, t.split(/\n|,|;/).filter(function (s) { return s.trim(); }).length) : 0;
    }
    // guests without picked products: the error belongs to the product search field
    return { picked: picked, count: count, target: text || checks[0] || $('[data-wd-search-input]', form) };
  }
  function withdrawalRef() {
    return 'HR-' + Date.now().toString(36).toUpperCase() + '-' +
      Math.floor(Math.random() * 46656).toString(36).toUpperCase().padStart(3, '0');
  }

  // Fill the hidden fields (withdrawal: reference, products, body) and return the values for the
  // success message. Runs once per (valid) submit, before any post.
  function prepareForm(form, kind) {
    var get = function (name) { var el = form.elements[name]; return el ? String(el.value || '').trim() : ''; };
    var values = { name: get('contact[name]'), email: get('contact[email]') };
    if (kind === 'withdrawal') {
      var wd = withdrawalPrepare(form);
      var ref = withdrawalRef();
      var refInput = $('[data-wd-ref]', form);
      if (refInput) refInput.value = ref;
      var prodInput = $('[data-wd-products]', form);
      if (prodInput) prodInput.value = wd.picked.join(', ');
      var body = $('[data-wd-body]', form);
      if (body) {
        body.value = [
          'EU-herroeping ' + ref,
          'Ordernummer: ' + get('contact[Ordernummer]'),
          'Producten: ' + wd.picked.join(', '),
          'Reden: ' + get('contact[Reden]'),
          'Toelichting: ' + get('contact[Toelichting]')
        ].join('\n');
      }
      var p = $('[data-success-text]', form);
      var one = p ? p.getAttribute('data-one') : '1 product';
      var many = p ? p.getAttribute('data-many') : '{n} producten';
      values.count = wd.count === 1 ? one : fill(many, { n: wd.count });
      values.order = get('contact[Ordernummer]');
      values.ref = ref;
    }
    return values;
  }

  // Validated + prepared in the capture phase above; here: post (or leave it to Shopify's captcha).
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[data-ajax-form]')) return;
    if (form._ghNative) return; // native fallback (bot challenge)
    var values = form._ghValues || prepareForm(form, form.getAttribute('data-ajax-form'));
    var submit = formSubmitButton(form);
    if (captchaManaged(form)) {
      // Shopify's hCaptcha has taken over this submit (it posts natively after the check)
      stashPosted(form, values);
      pendingWhileNative(submit);
      return;
    }
    e.preventDefault();
    setPending(submit, true);
    postForm(form)
      .then(function (result) {
        if (result && result.ok) { showFormSuccess(form, values); return; }
        if (result === 'challenge') { nativeSubmit(form, values); return; }
        fieldError(form, (result && result.error) || str('error'));
      })
      .catch(function () { fieldError(form, str('error')); })
      .then(function () { setPending(submit, false); });
  });

  // Return from a native post (no JS, or Shopify's captcha / challenge page): re-open the modal that was
  // submitted, or put the scratch card in its done state. The form is found by the URL hash (the forms
  // post to /contact#<id>) or by what we stashed before the native post.
  function peekPosted() {
    try {
      var s = JSON.parse(window.sessionStorage.getItem(POSTED_KEY) || 'null');
      return s && Date.now() - s.t < 30 * 60 * 1000 ? s : null;
    } catch (e) { return null; }
  }
  function restorePostedForms() {
    if (/\/challenge/.test(window.location.pathname)) return; // still on Shopify's challenge page
    var stash = peekPosted();
    var hash = window.location.hash.replace('#', '');
    var postedUrl = /[?&](contact|customer)_posted=true/.test(window.location.search);
    var id = hash && document.getElementById(hash) ? hash : (stash ? stash.id : '');
    var form = id && document.getElementById(id);
    if (!form || form.tagName !== 'FORM') return;
    var fromStash = !!(stash && stash.id === id);
    var posted = !!$('[data-form-posted]', form) && (postedUrl || hash === id) && (hash === id || fromStash);
    if (form.hasAttribute('data-sc-form')) {
      var scErr = $('[data-sc-error]:not([hidden])', form);
      if (posted || scErr) { if (fromStash) takePosted(); scRestore(form, posted); }
      return;
    }
    if (!form.matches('form[data-ajax-form]')) return;
    var modal = form.closest('[data-drawer]');
    if (!modal) return;
    var hasError = $('[data-form-error]:not([hidden])', form);
    if (!posted && !hasError) return;
    if (fromStash) takePosted();
    modal._ghKeepSuccess = posted;
    modal._ghKeepError = !posted;
    if (posted) showFormSuccess(form, fromStash ? stash.values : {});
    GH.drawer.open(modal.getAttribute('data-drawer'));
  }

  /* ------------------------------------------------------------ scratch card */
  // The discount code is not in the page until the subscription is confirmed: Shopify renders it inside
  // the scratch form ([data-sc-code-value]) only when form.posted_successfully?. We read it from the
  // answer to our POST (or the page after a native post) and keep it in localStorage ('gh:scratch-code').
  var SC_KEY = 'gh:scratch';
  var SC_CODE_KEY = 'gh:scratch-code';
  var sc = { inited: false, scratched: false, done: false, code: '' };

  function scRoot() { return $('[data-scratch]'); }

  function scCodeFrom(doc) {
    var el = doc && doc.querySelector('#ScratchForm [data-sc-code-value], [data-sc-code-value]');
    return el ? el.getAttribute('data-sc-code-value') || '' : '';
  }
  function scApplyCode(root, code) {
    if (!code) return;
    sc.code = code;
    storage.set(SC_CODE_KEY, code);
    var t = $('[data-sc-code-text]', root);
    if (t) t.textContent = code;
  }
  // Returning subscriber without a stored code (subscribed before this version): ask the section.
  function scFetchCode(root) {
    return fetch((GH.routes.root || '/') + '?customer_posted=true&section_id=scratch-card', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var code = scCodeFrom(new DOMParser().parseFromString(html, 'text/html'));
        scApplyCode(root, code);
        return code;
      })
      .catch(function () { return ''; });
  }

  function scCopy(root) {
    var code = sc.code || '';
    var label = $('[data-sc-copy-label]', root);
    var icon = $('[data-sc-copy-icon]', root);
    var note = $('[data-sc-note]', root);
    var text = $('[data-sc-text]', root);
    function result(ok) {
      if (label) label.textContent = ok ? str('copied', 'Gekopieerd') : str('copy', 'Kopieer');
      if (icon) icon.setAttribute('d', ok ? 'M5 12l5 5 9-10' : 'M9 9h11v11H9z M5 15H4V4h11v1');
      if (note) note.textContent = ok ? str('copyNoteOk') : str('copyNoteFail');
      if (text && sc.done) text.textContent = root.getAttribute(ok ? 'data-text-done-copied' : 'data-text-done');
    }
    function fallback() {
      try {
        var ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        ta.remove();
        result(!!ok);
      } catch (err) { result(false); }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(function () { result(true); }, fallback);
    else fallback();
  }

  function scShowStep(root) {
    var canvas = $('[data-sc-canvas]', root);
    if (canvas) {
      canvas.classList.add('is-cleared');
      canvas.tabIndex = -1; // scratched away: no longer a control
      canvas.setAttribute('aria-hidden', 'true');
    }
    var hint = $('[data-sc-hint]', root);
    if (hint) hint.hidden = true;
    var step = $('[data-sc-step]', root);
    if (step) step.hidden = false;
  }

  function scFinishScratch(root) {
    if (sc.scratched) return;
    sc.scratched = true;
    var canvas = $('[data-sc-canvas]', root);
    if (canvas) canvas.classList.add('is-cleared');
    setTimeout(function () {
      scShowStep(root);
      var email = $('[data-sc-email]', root);
      if (email && !sc.done) email.focus();
    }, 450);
  }

  // Only called once the subscription is confirmed (our POST, or Shopify's page after a native post).
  function scSetDone(root, copy) {
    sc.done = true;
    sc.scratched = true;
    if (!sc.code) scApplyCode(root, storage.get(SC_CODE_KEY) || '');
    if (!sc.code) scFetchCode(root);
    scShowStep(root);
    var panel = $('.gh-sc', root);
    if (panel) panel.classList.add('is-done');
    var email = $('[data-sc-email]', root);
    if (email) { email.readOnly = true; email.tabIndex = -1; }
    var codeBtn = $('[data-sc-copy]', root);
    if (codeBtn) { codeBtn.tabIndex = 0; codeBtn.removeAttribute('aria-hidden'); }
    var done = $('[data-sc-done]', root);
    if (done) done.hidden = false;
    var text = $('[data-sc-text]', root);
    if (text) text.textContent = root.getAttribute('data-text-done');
    storage.set(SC_KEY, 'done');
    if (copy) scCopy(root);
    if (codeBtn) codeBtn.focus();
  }

  function scInitCanvas(root) {
    var canvas = $('[data-sc-canvas]', root);
    if (!canvas || sc.inited || sc.scratched) return;
    var w = canvas.offsetWidth, h = canvas.offsetHeight;
    if (!w || !h) return;
    sc.inited = true;
    var dpr = 2;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    var x = canvas.getContext('2d');
    if (!x) { scFinishScratch(root); return; }
    x.scale(dpr, dpr);
    var g = x.createLinearGradient(0, 0, w, h);
    [[0, '#8C6A2A'], [0.18, '#D4AF55'], [0.34, '#F7E9B0'], [0.48, '#BE9436'], [0.64, '#EED48A'], [0.8, '#9C7629'], [1, '#E2C26E']].forEach(function (s) { g.addColorStop(s[0], s[1]); });
    x.fillStyle = g;
    x.fillRect(0, 0, w, h);
    for (var i = 0; i < h; i += 1) {
      x.fillStyle = 'rgba(' + (Math.random() > 0.5 ? '255,248,220,' : '90,62,18,') + (Math.random() * 0.08).toFixed(3) + ')';
      x.fillRect(0, i, w, 1);
    }
    [[0.15, 0.22], [0.52, 0.14], [0.78, 0.18]].forEach(function (s) {
      var cx = w * s[0];
      var sh = x.createLinearGradient(cx - 60, 0, cx + 60, h);
      sh.addColorStop(0, 'rgba(255,255,255,0)');
      sh.addColorStop(0.5, 'rgba(255,255,255,' + s[1] + ')');
      sh.addColorStop(1, 'rgba(255,255,255,0)');
      x.save(); x.translate(cx, h / 2); x.rotate(-0.5); x.fillStyle = sh; x.fillRect(-70, -h, 140, h * 2); x.restore();
    });
    for (var k = 0; k < 1400; k++) {
      x.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,240,0.5)' : 'rgba(70,48,12,0.25)';
      x.fillRect(Math.random() * w, Math.random() * h, 1, 1);
    }
    x.strokeStyle = 'rgba(90,62,18,0.45)';
    x.setLineDash([4, 4]); x.lineWidth = 1; x.strokeRect(10.5, 10.5, w - 21, h - 21); x.setLineDash([]);
    x.textAlign = 'center';
    var main = root.getAttribute('data-label-main') || '';
    var sub = root.getAttribute('data-label-sub') || '';
    x.font = "800 20px 'Plus Jakarta Sans', sans-serif";
    x.fillStyle = 'rgba(255,250,225,0.7)'; x.fillText(main, w / 2 + 1, h / 2 + 3);
    x.fillStyle = '#4A3410'; x.fillText(main, w / 2, h / 2 + 2);
    x.font = "600 12px 'Plus Jakarta Sans', sans-serif";
    x.fillStyle = 'rgba(74,52,16,0.85)'; x.fillText(sub, w / 2, h / 2 + 22);
    x.globalCompositeOperation = 'destination-out';

    var down = false, last = null, n = 0, R = 19;
    function pos(ev) {
      var r = canvas.getBoundingClientRect();
      return { x: (ev.clientX - r.left) * (w / r.width), y: (ev.clientY - r.top) * (h / r.height) };
    }
    function check() {
      var d = x.getImageData(0, 0, canvas.width, canvas.height).data;
      var clear = 0, tot = 0;
      for (var j = 3; j < d.length; j += 4 * 24) { tot++; if (d[j] < 128) clear++; }
      if (tot && clear / tot > 0.45) scFinishScratch(root);
    }
    function stamp(px, py) {
      var rg = x.createRadialGradient(px, py, 0, px, py, R + 5);
      rg.addColorStop(0, 'rgba(0,0,0,1)'); rg.addColorStop(0.72, 'rgba(0,0,0,1)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = rg; x.beginPath(); x.arc(px, py, R + 5, 0, Math.PI * 2); x.fill();
    }
    function seg(a, b) {
      var len = Math.hypot(b.x - a.x, b.y - a.y), steps = Math.max(1, Math.ceil(len / 3));
      for (var s = 0; s <= steps; s++) stamp(a.x + (b.x - a.x) * s / steps, a.y + (b.y - a.y) * s / steps);
    }
    canvas.addEventListener('pointerdown', function (ev) {
      if (sc.scratched) return;
      down = true;
      last = pos(ev);
      try { canvas.setPointerCapture(ev.pointerId); } catch (err) { /* ignore */ }
      seg(last, { x: last.x + 0.5, y: last.y + 0.5 });
    });
    canvas.addEventListener('pointermove', function (ev) {
      if (!down || sc.scratched) return;
      var p = pos(ev);
      seg(last, p);
      last = p;
      if (++n % 8 === 0) check();
    });
    function up() { if (down) { down = false; check(); } }
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
  }

  // Auto-open only when the visitor isn't busy: no overlay, search or mega menu open, not typing in a
  // field (and no key pressed in the last 2 s). Otherwise try again a little later.
  var scLastKey = 0;
  document.addEventListener('keydown', function () { scLastKey = Date.now(); }, true);
  function scBusy() {
    if (openStack.length || $('.gh-search.is-open') || $('.gh-nav__item.is-open')) return true;
    var a = document.activeElement;
    if (a && a !== document.body && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return true;
    return Date.now() - scLastKey < 2000;
  }
  var scAutoTimer;
  function scAutoOpen(ms) {
    clearTimeout(scAutoTimer);
    scAutoTimer = setTimeout(function () {
      if (storage.get(SC_KEY) || !scRoot()) return;
      if (scBusy()) { scAutoOpen(3000); return; }
      GH.drawer.open('scratch');
    }, ms);
  }

  function scInit() {
    var root = scRoot();
    if (!root) return;
    var state = storage.get(SC_KEY);
    if (state === 'done') scSetDone(root, false);
    var delay = parseInt(root.getAttribute('data-delay'), 10) || 0;
    var designMode = window.Shopify && window.Shopify.designMode;
    if (!state && delay > 0 && !designMode) scAutoOpen(delay * 1000);
  }

  // Back from a native post of the scratch form (Shopify's captcha / challenge page)
  function scRestore(form, posted) {
    var root = scRoot();
    if (!root) return;
    clearTimeout(scAutoTimer);
    if (posted) {
      scApplyCode(root, scCodeFrom(document));
      scSetDone(root, false);
    } else {
      sc.scratched = true;
      scShowStep(root); // show the form again with Shopify's error message
    }
    GH.drawer.open('scratch');
  }

  document.addEventListener('gh:drawer:open', function (e) {
    if (e.detail.name !== 'scratch') return;
    var root = scRoot();
    if (!root) return;
    var tab = $('[data-sc-tab]');
    if (tab) tab.classList.add('is-open');
    requestAnimationFrame(function () { scInitCanvas(root); });
  });
  document.addEventListener('gh:drawer:close', function (e) {
    if (e.detail.name !== 'scratch') return;
    if (!sc.done && !storage.get(SC_KEY)) storage.set(SC_KEY, 'closed');
  });
  document.addEventListener('click', function (e) {
    var root = e.target.closest('[data-scratch]');
    if (!root) return;
    if (e.target.closest('[data-sc-copy]')) { e.preventDefault(); if (sc.done) scCopy(root); return; }
    if (e.target.closest('[data-sc-reveal]')) { e.preventDefault(); scFinishScratch(root); return; }
    if (e.target.closest('[data-sc-shop]')) {
      e.preventDefault();
      var code = sc.code || '';
      var shopUrl = root.getAttribute('data-shop-url');
      var applied = fill(root.getAttribute('data-applied'), { code: code });
      var go = function () {
        GH.drawer.close('scratch');
        GH.toast(applied);
        if (shopUrl && window.location.pathname !== shopUrl) {
          setTimeout(function () { window.location.href = shopUrl; }, 900);
        }
      };
      if (code) fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(function () {}).then(go);
      else go();
    }
  });
  // Keyboard / no-pointer path: the canvas is a button (Enter or Space scratches it all away)
  document.addEventListener('keydown', function (e) {
    var canvas = e.target.closest && e.target.closest('[data-sc-canvas]');
    if (!canvas || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) return;
    e.preventDefault();
    var root = canvas.closest('[data-scratch]');
    if (root) scFinishScratch(root);
  });

  function scError(root, message) {
    var email = $('[data-sc-email]', root);
    var err = $('[data-sc-error]', root);
    if (err) { err.textContent = message || ''; err.hidden = !message; }
    if (!email) return;
    if (message) {
      email.setAttribute('aria-invalid', 'true');
      if (err) describe(email, err.id, true);
      email.focus();
    } else {
      email.removeAttribute('aria-invalid');
      if (err) describe(email, err.id, false);
    }
  }
  // Validate first (capture phase), so Shopify's captcha never starts on an invalid address
  window.addEventListener('submit', function (e) {
    var form = e.target;
    var root = form && form.closest && form.closest('[data-scratch]');
    if (!root || form._ghNative) return;
    var email = $('[data-sc-email]', root);
    var v = email ? email.value.trim() : '';
    if (sc.done || !EMAIL_RE.test(v)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!sc.done) scError(root, str('emailInvalid'));
      return;
    }
    scError(root, '');
  }, true);
  document.addEventListener('input', function (e) {
    var root = e.target.matches && e.target.matches('[data-sc-email]') && e.target.closest('[data-scratch]');
    var err = root && $('[data-sc-error]', root);
    if (err && !err.hidden) scError(root, '');
  });
  document.addEventListener('submit', function (e) {
    var form = e.target;
    var root = form.closest && form.closest('[data-scratch]');
    if (!root || !form.matches('form') || form._ghNative) return;
    var btn = $('[data-sc-submit]', root);
    if (captchaManaged(form)) {
      // Shopify's hCaptcha posts the form natively; the card shows the code after the redirect back
      stashPosted(form, {});
      pendingWhileNative(btn);
      return;
    }
    e.preventDefault();
    setPending(btn, true);
    // The code is only shown (and gh:scratch=done only stored) once Shopify confirms the subscription
    postForm(form)
      .then(function (result) {
        if (result && result.ok) {
          scApplyCode(root, scCodeFrom(result.doc));
          scSetDone(root, true);
          return;
        }
        if (result === 'challenge') { nativeSubmit(form, {}); return; }
        scError(root, (result && result.error) || str('error'));
      })
      .catch(function () { scError(root, str('error')); })
      .then(function () { setPending(btn, false); });
  });

  /* ------------------------------------------- footer accordions + gift tab */
  // Footer menus are accordions on mobile only. On desktop the panels are always open, so the titles
  // are not controls there: aria-expanded="true" and disabled (not focusable, nothing to toggle).
  var desktopMq = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
  function footerAccSync() {
    var desk = desktopMq ? desktopMq.matches : true;
    $$('.gh-footer__nav-title[data-accordion]').forEach(function (btn) {
      if (desk) {
        if (!btn.disabled) btn._ghMobileExpanded = btn.getAttribute('aria-expanded');
        btn.setAttribute('aria-expanded', 'true');
        btn.disabled = true;
      } else if (btn.disabled) {
        btn.disabled = false;
        btn.setAttribute('aria-expanded', btn._ghMobileExpanded || 'false');
      }
    });
  }
  if (desktopMq) {
    if (desktopMq.addEventListener) desktopMq.addEventListener('change', footerAccSync);
    else if (desktopMq.addListener) desktopMq.addListener(footerAccSync);
  }

  // The floating gift tab tucks away while the footer's bottom bar (legal text, payment icons) is in view.
  var scTabObserver;
  function scTabWatch() {
    var tab = $('[data-sc-tab]');
    var foot = $('.gh-footer__bottom');
    if (scTabObserver) { scTabObserver.disconnect(); scTabObserver = null; }
    if (!tab || !foot || !('IntersectionObserver' in window)) return;
    scTabObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { tab.classList.toggle('is-tucked', en.isIntersecting); });
    });
    scTabObserver.observe(foot);
  }

  /* ---------------------------------------------------- init + editor hooks */
  var observer;
  function init(root) {
    GH.favorites.sync(root);
    GH.carousels.update(root);
    announceInit(root);
    footerAccSync();
  }

  function boot() {
    init();
    GH.favorites.render();
    scInit();
    restorePostedForms();
    scTabWatch();
    if ('MutationObserver' in window) {
      var pending = false;
      observer = new MutationObserver(function (mutations) {
        if (pending) return;
        var relevant = mutations.some(function (m) { return m.addedNodes && m.addedNodes.length; });
        if (!relevant) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          GH.favorites.sync();
          GH.carousels.update();
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    if ($('[data-fav-list]', e.target)) GH.favorites.render();
    if ($('[data-scratch]', e.target)) { sc.inited = false; sc.scratched = false; sc.done = false; sc.code = ''; scInit(); }
    if ($('[data-sc-tab]', e.target) || $('.gh-footer__bottom', e.target)) scTabWatch();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    openStack.slice().forEach(function (o) { if (e.target.contains(o.el)) closeEntry(o, false); });
  });
  document.addEventListener('shopify:section:select', function (e) {
    if ($('[data-scratch]', e.target)) GH.drawer.open('scratch');
    if ($('[data-cart-drawer-inner]', e.target)) GH.drawer.open('cart');
  });
  document.addEventListener('shopify:block:select', function (e) {
    var target = e.target;
    // announcement bar: show the selected message and hold it
    var slide = target.closest && target.closest('[data-announce-slide]');
    var bar = slide && slide.closest('[data-announce]');
    if (bar) {
      var st = announceState(bar);
      st.editor = true;
      announceGo(bar, $$('[data-announce-slide]', bar).indexOf(slide));
      announceSchedule(bar);
      return;
    }
    // header "Menu-afbeelding" block: open the mega panel (desktop) or the mobile sub-panel
    var id = e.detail && e.detail.blockId;
    if (!id || !(target.closest && target.closest('.gh-header-wrap'))) return;
    var desktopTile = $('.gh-mega [data-gh-menu-block="' + id + '"]') || (target.closest('.gh-mega') && target);
    var mobileTile = $('[data-mmenu] [data-gh-menu-block="' + id + '"]');
    var desktop = window.matchMedia ? window.matchMedia('(min-width: 990px)').matches : true;
    if (desktop && desktopTile) {
      openMega(desktopTile.closest('.gh-nav__item'));
    } else if (!desktop && mobileTile) {
      var view = mobileTile.closest('[data-mmenu-view]');
      var viewName = view ? view.getAttribute('data-mmenu-view') : (mobileTile.closest('[data-mmenu-top]') ? 'cats' : null);
      if (!viewName) return;
      if (GH.drawer.isOpen('menu')) menuShow(viewName, null);
      else { menuNextView = viewName; GH.drawer.open('menu'); }
      if (!view) mobileTile.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announce]');
    if (bar) { announceState(bar).editor = false; announceSchedule(bar); return; }
    if (e.target.closest && e.target.closest('.gh-header-wrap')) closeMega();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if ($('[data-scratch]', e.target)) GH.drawer.close('scratch');
    if ($('[data-cart-drawer-inner]', e.target)) GH.drawer.close('cart');
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
